import * as React from 'react';
import type { RunTarget } from '@chatsdet/types';
import { API_URL } from '../../config';
import {
  useWorkflowTestStream,
  type WorkflowTestStreamState,
  type ConnectionState,
} from '../../hooks/useWorkflowTestStream';

export type LiveRun = {
  suiteId: string;
  startedAt: string | null;
  /**
   * What the run was launched against (suite/schedule/tag/ad hoc), resolved
   * server-side. Null for a run added optimistically via `addRun` before the
   * next discovery fetch resolves it — the live panel falls back to a generic
   * label rather than inventing one from the id.
   */
  runTarget: RunTarget | null;
  executionStatus: 'idle' | 'running' | 'completed';
  executionDurationMs: number;
  executionState: WorkflowTestStreamState;
  connectionState: ConnectionState;
  retryConnection: () => void;
};

type LiveWorkflowTestExecutions = {
  runs: LiveRun[];
  selectedRunId: string | null;
  setSelectedRunId: (id: string | null) => void;
  addRun: (suiteId: string, startedAt?: string | null) => void;
  /**
   * Re-discover active runs via a short polling burst. Use after triggering a
   * run whose execution row is created asynchronously (e.g. a "Run now"
   * schedule trigger) so the new live run surfaces without a page refresh.
   */
  refresh: () => void;
};

type RunEntry = {
  suiteId: string;
  startedAt: string | null;
  runTarget: RunTarget | null;
  addedAt: number;
};

/**
 * Manages multiple concurrent live SSE connections for workflow test executions.
 *
 * 1. On mount, fetches GET /workflows/test-runs/active-runs to discover running executions.
 * 2. For each, opens an SSE stream via useWorkflowTestStream.
 * 3. Completed runs auto-removed after 5s.
 * 4. selectedRunId defaults to most recent.
 */
export function useLiveWorkflowTestExecutions(orgId: string | null): LiveWorkflowTestExecutions {
  const [runEntries, setRunEntries] = React.useState<RunEntry[]>([]);
  const [selectedRunId, setSelectedRunId] = React.useState<string | null>(null);
  const [tick, setTick] = React.useState(0);
  const [refreshNonce, setRefreshNonce] = React.useState(0);

  // Mirror current entries into a ref so async discovery can detect which runs
  // are newly-appeared without reading state inside a setState updater.
  const runEntriesRef = React.useRef<RunEntry[]>(runEntries);
  runEntriesRef.current = runEntries;

  // Fetch GET /workflows/test-runs/active-runs and merge any not-yet-tracked
  // executions (dedup by suiteId). Returns true if at least one new run was
  // discovered. When `selectNew` is set, also selects the most-recent freshly
  // discovered run so a manual refresh surfaces what the user just triggered.
  const discoverActiveRuns = React.useCallback(
    async (signal: AbortSignal, { selectNew }: { selectNew: boolean }): Promise<boolean> => {
      try {
        const response = await fetch(`${API_URL}/workflows/test-runs/active-runs`, {
          credentials: 'include',
          signal,
        });
        if (!response.ok) return false;
        const data = (await response.json()) as {
          ok: boolean;
          executions?: Array<{
            id: string;
            startedAt?: string | null;
            runTarget?: RunTarget;
          }>;
        };
        if (!data.ok || !data.executions || data.executions.length === 0) return false;

        // active-runs is ordered createdAt DESC, so [0] is the most recent.
        const entries: RunEntry[] = data.executions.map((exec) => ({
          suiteId: exec.id,
          startedAt: exec.startedAt ?? null,
          runTarget: exec.runTarget ?? null,
          addedAt: Date.now(),
        }));
        const knownIds = new Set(runEntriesRef.current.map((e) => e.suiteId));
        const freshEntries = entries.filter((e) => !knownIds.has(e.suiteId));

        // A run added optimistically by `addRun` has no target yet, and it is
        // already in `knownIds` — so merging only the fresh entries would leave
        // the run the user just started unnamed until a full page reload.
        // Backfill targets onto entries we already track, then append the
        // genuinely new ones.
        const discoveredTargets = new Map(entries.map((e) => [e.suiteId, e.runTarget]));
        const needsBackfill = runEntriesRef.current.some(
          (e) => !e.runTarget && discoveredTargets.get(e.suiteId)
        );

        if (freshEntries.length > 0 || needsBackfill) {
          setRunEntries((prev) => {
            const existingIds = new Set(prev.map((e) => e.suiteId));
            const newEntries = entries.filter((e) => !existingIds.has(e.suiteId));
            // Only replace an entry's identity when a target was actually
            // discovered for it — an entry that's still unresolved (`null` ->
            // `null`) keeps its existing object so this setState is skipped
            // entirely when nothing actually changed (see the `freshEntries.length
            // > 0 || needsBackfill` guard above), avoiding a pointless
            // re-render. `activeEntries` (and the `runs` memo below) are
            // recomputed fresh every render regardless — `runEntries.slice(0, 5)`
            // is a new array each time — so this does not "preserve identity"
            // for any downstream memoization; it only avoids an unnecessary
            // state update.
            const backfilled = prev.map((e) => {
              if (e.runTarget) return e;
              const discovered = discoveredTargets.get(e.suiteId);
              return discovered ? { ...e, runTarget: discovered } : e;
            });
            return selectNew ? [...newEntries, ...backfilled] : [...backfilled, ...newEntries];
          });
        }

        // Default selection to most recent when nothing is selected yet.
        setSelectedRunId((prev) => prev ?? entries[0]?.suiteId ?? null);

        if (selectNew && freshEntries[0]) {
          setSelectedRunId(freshEntries[0].suiteId);
        }

        // The refresh() polling burst (below) exists to wait for a run
        // triggered from elsewhere on the page (e.g. a schedule's "Run now")
        // whose execution row is created asynchronously — it must keep
        // polling until THAT run shows up as a fresh entry. A backfill only
        // resolves the target on a run we already know about (e.g. the
        // dialog's optimistic `addRun` entry) and says nothing about whether
        // the newly-triggered run has appeared yet, so it must not end the
        // burst early: doing so previously let one run's backfill mask
        // another run's execution row never having been discovered.
        return freshEntries.length > 0;
      } catch (error) {
        if ((error as DOMException)?.name === 'AbortError') return false;
        // A persistently failing discovery would otherwise silently never
        // populate the rail — leave a breadcrumb without surfacing a toast
        // for what is, on any single failure, an unremarkable network blip.
        console.debug('Failed to discover active test runs', error);
        return false;
      }
    },
    []
  );

  // Discover active runs on mount.
  React.useEffect(() => {
    if (!orgId) return;
    const controller = new AbortController();
    void discoverActiveRuns(controller.signal, { selectNew: false });
    return () => controller.abort();
  }, [orgId, discoverActiveRuns]);

  // Polling burst after a manual refresh(). The triggered execution row is
  // created asynchronously (when the orchestrator job starts), so a single
  // re-fetch would usually miss it; poll a few times until it appears.
  React.useEffect(() => {
    if (refreshNonce === 0 || !orgId) return;

    const controller = new AbortController();
    let cancelled = false;
    let attempts = 0;
    let timer: number | undefined;

    const poll = async () => {
      attempts += 1;
      const foundNew = await discoverActiveRuns(controller.signal, { selectNew: true });
      if (cancelled || foundNew || attempts >= 6) return;
      timer = window.setTimeout(() => void poll(), 2500);
    };

    void poll();

    return () => {
      cancelled = true;
      controller.abort();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [refreshNonce, orgId, discoverActiveRuns]);

  const refresh = React.useCallback(() => {
    setRefreshNonce((n) => n + 1);
  }, []);

  const addRun = React.useCallback((suiteId: string, startedAt?: string | null) => {
    setRunEntries((prev) => {
      if (prev.some((e) => e.suiteId === suiteId)) return prev;
      return [
        // Added optimistically, ahead of any fetch — the run's target isn't
        // known yet, so this stays null rather than guessing from the id.
        {
          suiteId,
          startedAt: startedAt ?? new Date().toISOString(),
          runTarget: null,
          addedAt: Date.now(),
        },
        ...prev,
      ];
    });
    setSelectedRunId(suiteId);
  }, []);

  // Use SSE streams for up to 5 runs (to limit open connections)
  const activeEntries = runEntries.slice(0, 5);

  // Individual SSE streams
  const stream0 = useWorkflowTestStream(activeEntries[0]?.suiteId ?? null);
  const stream1 = useWorkflowTestStream(activeEntries[1]?.suiteId ?? null);
  const stream2 = useWorkflowTestStream(activeEntries[2]?.suiteId ?? null);
  const stream3 = useWorkflowTestStream(activeEntries[3]?.suiteId ?? null);
  const stream4 = useWorkflowTestStream(activeEntries[4]?.suiteId ?? null);
  const streams = [stream0, stream1, stream2, stream3, stream4];

  // Stable dependency keys computed once per render
  const statusKey = streams.map((s) => s.status).join(',');
  const completedKey = streams.map((s) => s.completedWorkflows).join(',');

  // Duration tick for running executions
  React.useEffect(() => {
    const hasRunning = activeEntries.some((_, i) => {
      const stream = streams[i];
      return stream && stream.status !== 'completed';
    });
    if (!hasRunning) return;
    const id = window.setInterval(() => {
      setTick((v) => (v + 1) % Number.MAX_SAFE_INTEGER);
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEntries.length, statusKey]);

  // Build LiveRun array
  const runs: LiveRun[] = React.useMemo(() => {
    return activeEntries.map((entry, i) => {
      const state = streams[i]!;
      const executionStartedAt = state.startedAt ?? entry.startedAt;
      let executionStatus: 'idle' | 'running' | 'completed' = 'running';
      if (state.status === 'completed') {
        executionStatus = 'completed';
      } else if (state.status === 'idle' && entry.suiteId) {
        executionStatus = 'running';
      }

      let executionDurationMs = 0;
      if (executionStartedAt) {
        const started = new Date(executionStartedAt).getTime();
        if (executionStatus === 'completed' && state.finishedAt) {
          executionDurationMs = new Date(state.finishedAt).getTime() - started;
        } else {
          executionDurationMs = Date.now() - started;
        }
      }

      return {
        suiteId: entry.suiteId,
        startedAt: executionStartedAt,
        runTarget: entry.runTarget,
        executionStatus,
        executionDurationMs,
        executionState: state,
        connectionState: state.connectionState,
        retryConnection: state.retry,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEntries, tick, statusKey, completedKey]);

  // Stable key for completed-run tracking
  const runStatusKey = runs.map((r) => `${r.suiteId}:${r.executionStatus}`).join(',');

  // Auto-remove completed runs after 30s
  React.useEffect(() => {
    const completedRuns = runs.filter((r) => r.executionStatus === 'completed');
    if (completedRuns.length === 0) return;

    const timeouts = completedRuns.map((run) => {
      return window.setTimeout(() => {
        setRunEntries((prev) => prev.filter((e) => e.suiteId !== run.suiteId));
        setSelectedRunId((prev) => {
          if (prev === run.suiteId) {
            const remaining = runEntries.filter((e) => e.suiteId !== run.suiteId);
            return remaining[0]?.suiteId ?? null;
          }
          return prev;
        });
      }, 30000);
    });

    return () => timeouts.forEach((t) => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runStatusKey]);

  return {
    runs,
    selectedRunId,
    setSelectedRunId,
    addRun,
    refresh,
  };
}
